<template>
    <div class="w-full h-full flex flex-col p-2">
        <div>
            <div class="flex items-center justify-between">
                <div class="text-xl my-4">Import av kunder og leverandører</div>
                <div class="flex flex-col gap-2 items-end">
                    <UButton
                        size="sm"
                        icon="i-ph-download"
                        :color="importRows.length ? 'outline' : 'primary'"
                        @click="downloadSample"
                    >
                        Last ned eksempelfil
                    </UButton>
                </div>
            </div>
            <div class="flex gap-2 items-center my-2">
                <input
                    id="customer"
                    v-model="subAccountType"
                    type="radio"
                    class="radio radio-xs radio-primary"
                    :value="CUSTOMER"
                    autocomplete="off"
                >
                <label for="customer">Kunder</label>
            </div>
            <div class="flex gap-2 items-center">
                <input
                    id="supplier"
                    v-model="subAccountType"
                    type="radio"
                    class="sm radio radio-xs radio-primary"
                    :value="SUPPLIER"
                    autocomplete="off"
                >
                <label for="supplier">Leverandører</label>
            </div>
        </div>

        <FileUploadComponent
            @on-drop="onDrop"
            @on-upload="readFile"
            @on-pasted="onPaste"
            @clear-grid="() => importRows = []"
        />

        <ag-grid-vue
            class="grow"
            :theme="agTheme"
            :row-data="importRows"
            :grid-options="gridOptions"
            edit-type="fullRow"
            :column-defs="columnDefs"
            @dragover.prevent
            @drop.prevent="onDrop"
            @paste="onPaste"
        />
        <div class="flex justify-between p-2 gap-2">
            <UButton
                color="error"
                icon="i-ph-trash"
                :disabled="!selection.length"
                @click="deleteSelection"
            >
                Slett valgte ({{ selection.length }})
            </UButton>
            <UButton
                :color="selection.length ? 'primary' : 'neutral'"
                :disabled="!selection.length || isWorking"
                icon="i-ph-upload"
                @click="importSelection"
            >
                {{ subAccountType === CUSTOMER ? 'Importer kunder' : 'Importer leverandører' }} ({{ selection.length }})
            </UButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {ref, shallowRef} from 'vue'
import {ColDef, GridApi, GridOptions, ICellRendererParams, IRowNode, SelectionChangedEvent, ValueGetterParams} from 'ag-grid-community'
import {CUSTOMER, NewSubAccount, SubAccountRelationType, SUPPLIER} from '@/subaccount/types/SubAccount'
import * as XLSX from 'xlsx'
import {agTheme} from '@/agUtils/ag-theme.service'
import {createSubAccount, fetchCustomers, fetchSuppliers} from '@/subaccount/subAccount.service'
import {formattedDate} from '@/services/formatter.service'
import FileUploadComponent from '@/subaccount/FileUploadComponent.vue'
import {normalizeOrgNo} from '@/incoming-balance/incomingBalance.composable'
import {v4 as uuidv4} from 'uuid'

type SaccImport = {
    id: string
    name: string
    orgNo: string
    email: string
    phone: string
    contactPerson: string
    address: string
    postalCode: string
    postalLocation: string,
    importStatus: 'new' | 'success' | 'error'
}

const subAccountType = ref<SubAccountRelationType>(CUSTOMER)

const selection = ref<SaccImport[]>([])

const importRows = ref<SaccImport[]>([])

const isWorking = ref(false)

const gridApi = shallowRef<GridApi>()
const gridOptions: GridOptions = {
    getRowId: (params: { data: any }) => params.data.id,
    onRowEditingStopped: ({data}: any) => {
        const index = importRows.value.findIndex(row => row.id === data.id)
        importRows.value[index] = {...data, importStatus: 'new'}
    },
    onSelectionChanged: (event: SelectionChangedEvent) => {
        const selectedRows = event.api.getSelectedRows()
        selection.value = [...selectedRows]
    },
    onGridReady({api}: any) {
        gridApi.value = api
    },
    rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        isRowSelectable: (node: IRowNode<SaccImport>) => {
            return node.data?.importStatus !== 'success'
        }
    }
}

const columnDefs: ColDef[] = [
    {
        headerName: 'Status',
        field: 'importStatus',
        editable: false,
        width: 80,
        cellRenderer: (params: ICellRendererParams) => {
            if (params.value === 'new') {
                return 'Ny'
            } else if (params.value === 'success') {
                return '<i class="i-ph-check-circle text-success inline-block" title="Importert" />'
            } else if (params.value === 'error') {
                return '<i class="i-ph-x-circle text-error inline-block" title="Feil ved import" />'
            } else {
                return params.value
            }
        }
    },
    {
        headerName: 'Navn',
        field: 'name',
        editable: true
    },
    {
        headerName: 'Orgnr',
        field: 'orgNo',
        editable: true,
        width: 130,
        cellDataType: 'text',
        valueGetter: ({data}: ValueGetterParams) => {
            return data?.orgNo ? normalizeOrgNo(data.orgNo.toString()) : ''
        },
    },
    {
        headerName: 'E-post',
        field: 'email',
        editable: true
    },
    {
        headerName: 'Telefon',
        field: 'phone',
        editable: true,
        width: 120
    },
    {
        headerName: 'Adresse',
        field: 'address',
        editable: true,
    },
    {
        headerName: 'Postnr',
        field: 'postalCode',
        editable: true,
        width: 90,
        valueGetter: (val: ValueGetterParams) => {
            return ensureFourDigitPostalCode(val.data.postalCode)
        }
    },
    {
        headerName: 'Poststed',
        field: 'postalLocation',
        editable: true
    },
    {
        headerName: 'Kontaktperson',
        field: 'contactPerson',
        editable: true
    },
]

const deleteSelection = () => {
    const selectedIds = selection.value.map(row => row.id)
    importRows.value = importRows.value.filter(row => !selectedIds.includes(row.id))
}

const importSelection = async () => {
    isWorking.value = true
    const promises = []

    for (const importedRow of selection.value) {
        const subAccount: NewSubAccount = {
            name: importedRow.name,
            relationType: subAccountType.value,
            isBusiness: !!importedRow.orgNo,
            orgNo: importedRow.orgNo?.toString()?.replaceAll(' ', ''),
            contactInfo: {
                name: importedRow.contactPerson || importedRow.name,
                email: importedRow.email,
                phoneNumber: importedRow.phone
            },
            notes: 'Imported at ' + formattedDate(new Date().toString()),
            metadata: {suggestions: {expenseAccount: ''}},
            active: true
        }

        if (importedRow.postalCode && importedRow.postalLocation) {
            subAccount.address = {
                addressLine: importedRow.address || '',
                postalCode: ensureFourDigitPostalCode(importedRow.postalCode || '0000'),
                postalLocation: importedRow.postalLocation || ''
            }
        }

        const index = importRows.value.findIndex(row => row.id === importedRow.id)

        const promise = createSubAccount(subAccount)
            .then(() => {
                importedRow.importStatus = 'success'
            })
            .catch(() => {
                importedRow.importStatus = 'error'
            })
            .finally(() => {
                importRows.value[index] = {...importedRow}
            })

        promises.push(promise)
    }
    Promise.allSettled(promises)
        .then(() => {
            selection.value = []
            //TODO: Notify
            gridApi.value?.redrawRows()
            gridApi.value?.deselectAll()
            isWorking.value = false
            if (subAccountType.value === CUSTOMER) {
                fetchCustomers()
            } else {
                fetchSuppliers()
            }
        })
}

const ensureFourDigitPostalCode = (postalCode: string) => {
    if (postalCode.length === 4) {
        return postalCode
    } else if (postalCode.length > 4) {
        return postalCode.substring(0, 4)
    } else {
        return '0'.repeat(4 - postalCode.length) + postalCode
    }
}

const onDrop = async (event: any) => {
    const files = event.dataTransfer.files
    for (const file of files) {
        readFile(file)
    }
}

const readFile = (file: any) => {
    const reader = new FileReader()
    reader.onload = () => {
        const arrayBuffer = new Uint8Array(reader.result as ArrayBuffer)
        const workbook = XLSX.read(arrayBuffer)
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const xlRows = XLSX.utils.sheet_to_json(sheet)
        xlRows.forEach((row: any) => {
            const saccImport: SaccImport = {
                id: uuidv4(),
                name: row.name,
                orgNo: row.orgNo,
                email: row.email,
                phone: row.phone,
                contactPerson: row.contactPerson,
                address: row.address || '',
                postalCode: ensureFourDigitPostalCode(row.postalCode || '0000') as string,
                postalLocation: row.postalLocation || '',
                importStatus: 'new'
            }
            importRows.value.push(saccImport)
        })
        gridApi.value?.redrawRows()
        gridApi.value?.refreshCells()
        gridApi.value?.setGridOption('rowData', importRows.value)
    }
    reader.readAsArrayBuffer(file)
}

const downloadSample = () => {
    const url = 'Kunde-Lev-Import-eksempel.xlsx'
    const link = document.createElement('a')
    link.href = url
    link.download = url
    link.click()
}

const onPaste = (event: ClipboardEvent) => {
    try {

        const pastedDataToRows = event?.clipboardData?.getData('text/plain').split('\n').map((row: string): SaccImport => {
            const rowData = row.split('\t')
            return {
                id: uuidv4(),
                name: rowData[0],
                orgNo: rowData[1].toString().replaceAll(' ', ''),
                email: rowData[2],
                phone: rowData[3],
                contactPerson: rowData[4] || rowData[0],
                address: rowData[5],
                postalCode: ensureFourDigitPostalCode(rowData[6].toString()),
                postalLocation: rowData[7],
                importStatus: 'new'
            } as SaccImport
        })
        if (!pastedDataToRows) {
            return
        }
        importRows.value = [...importRows.value, ...pastedDataToRows]
    } catch (e) {
        console.error('Error pasting data', e)
    }

}


</script>

<style scoped>

</style>